import React from 'react';
import { motion } from 'framer-motion';

const AnimatedStars = () => (
  <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
    {[...Array(120)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute rounded-full bg-white opacity-30"
        style={{
          width: `${Math.random() * 2 + 1}px`,
          height: `${Math.random() * 2 + 1}px`,
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
        }}
        animate={{
          opacity: [0.2, 0.5, 0.2],
          y: [0, Math.random() * 20 - 10, 0],
        }}
        transition={{
          duration: Math.random() * 6 + 4,
          repeat: Infinity,
          repeatType: 'loop',
          delay: Math.random() * 4,
        }}
      />
    ))}
  </div>
);

const AnimatedPlanet = () => (
  <motion.div
    className="absolute left-1/2 top-1/2 z-10"
    style={{
      transform: 'translate(-50%, -50%)',
      filter: 'drop-shadow(0 0 80px #2563eb88)',
    }}
    initial={{ scale: 0.9, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ duration: 1.2, ease: 'easeOut' }}
  >
    <svg width="420" height="420" viewBox="0 0 420 420" fill="none">
      <defs>
        <radialGradient id="planetGradient" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.95" />
          <stop offset="60%" stopColor="#1e293b" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#020617" stopOpacity="1" />
        </radialGradient>
        <filter id="glow" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="24" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <circle cx="210" cy="210" r="160" fill="url(#planetGradient)" filter="url(#glow)" />
      <ellipse cx="210" cy="320" rx="120" ry="24" fill="#0ea5e9" opacity="0.12" />
      <ellipse cx="210" cy="320" rx="80" ry="12" fill="#3b82f6" opacity="0.08" />
      <ellipse cx="210" cy="320" rx="40" ry="6" fill="#fff" opacity="0.04" />
    </svg>
  </motion.div>
);

const HabitabilityAnalysis = ({ setActiveSection }) => {
  return (
    <div className="flex-1 bg-black min-h-screen relative overflow-hidden">
      <AnimatedStars />
      <AnimatedPlanet />
      <div className="relative z-20 flex flex-col items-center justify-center min-h-screen px-8">
        <div className="text-center max-w-5xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: 'easeOut' }}
            className="text-5xl md:text-6xl font-light text-white mb-12 leading-tight tracking-wide"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            Science-driven<br />
            <span className="font-normal gradient-text">Habitability Analysis</span>
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3, ease: 'easeOut' }}
            className="mb-16"
          >
            <p className="text-lg text-gray-300 max-w-4xl mx-auto leading-relaxed font-light tracking-wide mb-6"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
              Our algorithm integrates the Conservative Habitable Zone (CHZ) model, Earth Similarity Index (ESI),
              and atmospheric chemical equilibrium calculations.
            </p>
            <p className="text-sm text-gray-500 font-light tracking-wider"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
              All outputs are derived from peer-reviewed exoplanet research
            </p>
            <p className="text-xs text-gray-600 mt-2 font-light"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
              Sources: Kopparapu et al. 2013, Schulze-Makuch et al. 2011
            </p>
          </motion.div>
          <motion.button
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6, ease: 'easeOut' }}
            whileHover={{
              scale: 1.04,
              backgroundColor: 'rgba(59, 130, 246, 0.18)',
              borderColor: 'rgba(59, 130, 246, 0.7)'
            }}
            whileTap={{ scale: 0.98 }}
            className="border border-blue-700 hover:border-blue-500/80 text-white px-16 py-5 rounded-full font-light text-lg transition-all duration-300 mb-4 tracking-wider bg-blue-900/30 shadow-xl shadow-blue-900/30 backdrop-blur-md"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            onClick={() => setActiveSection && setActiveSection('calculate-habitability')}
          >
            START RESEARCH
          </motion.button>
        </div>
      </div>
    </div>
  );
};

export default HabitabilityAnalysis; 